import PropTypes from 'prop-types';
import React, { forwardRef, useState } from 'react';
// @mui
import { Box, Stack, Dialog, Grid, Button, AppBar, Typography, Container, Slide, Card } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useSettingsContext } from 'src/components/settings/context';
import { isEmpty } from 'lodash';
// store
import { setCurrentTreatmentDataGlobal } from 'src/redux/slices/multiTreatment';
import SimilarLeadWarningPopup from '../../sections/@dashboard/leadDetail/SimilarLeadWarningPopup';
import { dispatch, useSelector } from '../../redux/store';
import { updateShowSubjectList } from '../../redux/slices/practiceMessage';
// components
import { Archive } from '../tabs';
import { NormalLoading } from '../loading';
import Iconify from '../Iconify';
import { UnableToContact, FailedAppointment } from '../failedToAttendTabs';
import { clearLead, saveNewLeadDetail, FailedToAttendAppointment } from '../../redux/slices/lead';
import {
    commonDialogHeaderStyle,
    onBoardDialogHeader,
    reminderTitleStyle,
    cancelBoxStyle,
    reminderDialogStyle,
    attendedStackStyle,
    leadFooterButtonsStyle,
    PhoneIconButton,
    footerBoxStyle
} from '../../styles/ScheduleReminder';

const Transition = forwardRef((props, ref) => <Slide direction="up" ref={ref} {...props} />);

export default function OpenUnAttendedPopup({ open, onClose, type = 'lead', user, inputRef, headerButtons, handleCloseAndReload }) {

    const { followLead } = useSelector((state) => state.lead);
    const { practiceId } = useSelector((state) => state.practiceTreatmentList);

    const theme = useTheme();
    const isLight = theme.palette.mode === 'light';

    const settings = useSettingsContext();

    const [activeTab, setActiveTab] = useState(null);

    const handleClose = async (action) => {
        if (action) {
            onClose();
            dispatch(clearLead());
            dispatch(setCurrentTreatmentDataGlobal(null));
        }
    };

    const handleFailedToAttendClose = () => {
        if (handleCloseAndReload) {
            handleCloseAndReload('finish');
        } else {
            onClose('finish');
            dispatch(clearLead());
            dispatch(setCurrentTreatmentDataGlobal(null));
        }
    };

    const handleNext = async () => {
        dispatch(saveNewLeadDetail({ faildAttended: { activeTab } }));
        await inputRef.current.submit('finish');
    };

    const failedToAttendClick = () => {
        dispatch(saveNewLeadDetail({ faildAttended: { activeTab } }));
        const { lead_treatments: leadTreatment, lead_appointment: leadAppoiment } = followLead ?? {};
        if (!leadTreatment?.length) return;
        const appoinmentID = leadAppoiment?.filter((el) => el.status === 'BOOKED' || el.status === 'CONSULTATION_BOOKED');
        const appointmentDetail = {
            lead_treatment: leadTreatment[0].id,
        };
        dispatch(FailedToAttendAppointment(appointmentDetail, leadTreatment[0]?.lead, appoinmentID?.[0]?.id, practiceId, handleFailedToAttendClose, 'Unable to contact'));
    };

    const failedToAttendFinishSubmit = async () => {
        await inputRef.current.submit('');
    };

    const PROFILE_TABS = [
        {
            value: 'Unable to contact',
            icon: <Iconify icon={'akar-icons:circle-x-fill'} width={22} height={22} />,
            component: <UnableToContact ref={inputRef} type={type} handleNext={handleClose} completeFlow={handleFailedToAttendClose} />,
        },
        {
            value: 'Reschedule',
            icon: <Iconify icon={'eva:calendar-fill'} width={22} height={22} />,
            component: <FailedAppointment ref={inputRef} type={type} handleNext={handleClose} />,
        },
        {
            value: 'Archive',
            icon: <Iconify icon={'eva:archive-fill'} width={22} height={22} />,
            component: <Archive ref={inputRef} type={type} handleNext={handleClose} />,
        }
    ];

    if (!followLead) {
        return (
            <Dialog fullScreen TransitionComponent={Transition} open={open} onClose={handleClose} sx={reminderDialogStyle(type)}>
                <NormalLoading />
            </Dialog>
        );
    }

    return (
        <Dialog
            fullScreen
            TransitionComponent={Transition}
            open={open}
            onClose={handleClose}
            sx={[reminderDialogStyle(type), { pt: { xs: 0, sm: 0 }, '.MuiDialog-paperFullScreen': { bgcolor: '#F5F5F7', scrollbarGutter: 'stable' } }]}
        >
            <AppBar sx={[headerButtons ? commonDialogHeaderStyle : onBoardDialogHeader, { position: 'sticky !important', top: 0, alignItems: 'center' }]}>
                <Typography
                    sx={reminderTitleStyle}
                    variant="h6"
                    component="div"
                >
                    Unattended appointment for <span style={{ textTransform: 'capitalize' }}>{user.first_name}</span>
                </Typography>
                {headerButtons &&
                    <Box sx={cancelBoxStyle}>
                        <Button
                            color="inherit"
                            variant='outlined'
                            style={{ borderColor: theme.palette.common.white, color: theme.palette.common.white }}
                            onClick={() => { handleClose('close'); }}
                        >
                            Cancel
                        </Button>
                    </Box>
                }
            </AppBar>
            <Stack spacing={0} sx={{ ...attendedStackStyle, pt: { md: '24px', xs: '16px' }, pb: { md: '100px', xs: '100px' } }} onClick={() => { dispatch(updateShowSubjectList(false)); }}>
                <Grid container spacing={1} sx={{ maxWidth: '1088px', margin: 'auto' }}>
                    <Container
                        maxWidth={settings.themeStretch ? false : 'xl'}
                    >
                        <Typography
                            variant="subtitle1"
                            sx={{ textAlign: 'center', mb: 3, color: 'text.secondary', fontWeight: 500 }}
                        >
                            {user.treatment_name} appointment unattended — call <span style={{ textTransform: 'capitalize', fontWeight: 600, color: 'inherit' }}>{user.first_name} {user.last_name}</span> to find out why
                        </Typography>
                        <Card sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 3, borderRadius: '12px', border: 'none', maxWidth: '90vw', boxShadow: 'none', bgcolor: '#ffffff' }}>
                            <a
                                href={followLead && followLead.lead_contact && followLead.lead_contact.phone || ''}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    textDecoration: 'none',
                                    marginBottom: '10px',
                                    maxWidth: '100%'
                                }}
                            >
                                <PhoneIconButton>
                                    <Iconify style={{ color: theme.palette.common.green }} icon={'bxs:phone'} />
                                </PhoneIconButton>
                                {!isEmpty(followLead?.lead_contact?.phone) && <Typography variant="h1" color={isLight ? 'grey.800' : theme.palette.common.white}
                                    sx={{
                                        maxWidth: '100%',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        fontSize: { lg: '48px', md: '48px', sm: '3.25rem', xs: '1.2rem' },
                                    }}
                                >
                                    {followLead.lead_contact.phone}
                                </Typography>}
                            </a>
                            {type === 'recordUnAttended' && <SimilarLeadWarningPopup type={'DialogPopup'} dialogClose={() => handleClose('close')} />}
                            <Box sx={{ width: '100%', mt: 2 }}>
                                <Typography sx={{ fontSize: '22px', fontWeight: 700, color: '#1d1d1f', letterSpacing: '-0.022em', lineHeight: 1.2, textAlign: 'center' }}>
                                    What&apos;s next?
                                </Typography>
                                <Typography sx={{ fontSize: '13px', color: '#6e6e73', mt: 0.5, mb: 2, textAlign: 'center' }}>
                                    Find out why they didn&apos;t show up.
                                </Typography>
                                <Box
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                                        gap: 1.5,
                                        width: '100%',
                                    }}
                                >
                                    {PROFILE_TABS.map((tab) => {
                                        const isActive = activeTab === tab.value;
                                        const OUTCOME_META = {
                                            'Unable to contact': { desc: 'Couldn\'t reach them', color: '#FF5630', bg: 'rgba(255,86,48,0.06)' },
                                            'Reschedule': { desc: 'Book a new appointment', color: '#078DEE', bg: 'rgba(7,141,238,0.06)' },
                                            'Archive': { desc: 'Lead is not progressing', color: '#637381', bg: 'rgba(99,115,129,0.08)' },
                                        };
                                        const meta = OUTCOME_META[tab.value] || { desc: '', color: '#078DEE', bg: 'rgba(7,141,238,0.06)' };
                                        return (
                                            <Box
                                                key={tab.value}
                                                onClick={() => setActiveTab(tab.value)}
                                                sx={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                    gap: { xs: 0.5, sm: 1 },
                                                    px: { xs: 1, sm: 2 },
                                                    py: { xs: 1.5, sm: 2.5 },
                                                    borderRadius: '12px',
                                                    minHeight: { xs: 90, sm: 140 },
                                                    cursor: 'pointer',
                                                    transition: 'border-color 250ms cubic-bezier(0.32, 0.72, 0, 1), box-shadow 250ms cubic-bezier(0.32, 0.72, 0, 1), transform 350ms cubic-bezier(0.34, 1.56, 0.64, 1)',
                                                    background: meta.bg,
                                                    border: isActive ? `1.5px solid ${meta.color}` : '1px solid transparent',
                                                    boxShadow: isActive ? `0 0 0 1px ${meta.color}22` : 'none',
                                                    justifyContent: 'center',
                                                    userSelect: 'none',
                                                    '&:hover': { borderColor: meta.color, boxShadow: `0 0 0 1px ${meta.color}22` },
                                                    '&:active': { transform: 'scale(0.96)' },
                                                }}
                                            >
                                                <Box sx={{
                                                    width: { xs: 36, sm: 48 },
                                                    height: { xs: 36, sm: 48 },
                                                    borderRadius: { xs: '10px', sm: '12px' },
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    background: '#fff',
                                                    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                                                }}>
                                                    <Iconify icon={tab.icon.props.icon} width={{ xs: 18, sm: 24 }} sx={{ color: meta.color }} />
                                                </Box>
                                                <Typography sx={{ fontSize: { xs: '12px', sm: '14px' }, fontWeight: 600, color: '#212B36', whiteSpace: 'nowrap' }}>
                                                    {tab.value}
                                                </Typography>
                                                <Typography sx={{ fontSize: { xs: '10px', sm: '11px' }, color: '#919EAB', fontWeight: 400, textAlign: 'center', lineHeight: 1.3, display: { xs: 'none', sm: 'block' } }}>
                                                    {meta.desc}
                                                </Typography>
                                            </Box>
                                        );
                                    })}
                                </Box>
                                {activeTab && (
                                    <Box sx={{ mt: 3, width: '100%' }}>
                                        {PROFILE_TABS.find(t => t.value === activeTab)?.component}
                                    </Box>
                                )}
                            </Box>
                            {activeTab === 'Unable to contact' && user.status === 'FAILED_TO_ATTEND_CONSULTATION' &&
                                <Box sx={footerBoxStyle}>
                                    <Container
                                        maxWidth={settings.themeStretch ? false : 'xl'}
                                        sx={leadFooterButtonsStyle}
                                    >
                                        <Button
                                            color="primary"
                                            variant='contained'
                                            onClick={() => failedToAttendFinishSubmit()}
                                            disabled={!activeTab}
                                            sx={{
                                                bgcolor: '#00AB55',
                                                color: '#ffffff',
                                                fontSize: '13px',
                                                fontWeight: 600,
                                                letterSpacing: '-0.01em',
                                                textTransform: 'none',
                                                borderRadius: '6px',
                                                boxShadow: 'none',
                                                px: 2.25,
                                                py: 0.75,
                                                minWidth: '88px',
                                                '&:hover': { bgcolor: '#02934A', boxShadow: 'none' },
                                                '&:active': { bgcolor: '#017A3D' },
                                                '&.Mui-disabled': { bgcolor: 'rgba(0, 171, 85, 0.35)', color: '#ffffff' },
                                            }}
                                        >
                                            Finish
                                        </Button>
                                    </Container>
                                </Box>
                            }
                            {activeTab === 'Unable to contact' ?
                                <>
                                    {user.status === 'CONSULTATION_BOOKED'
                                        ?
                                        <Box sx={footerBoxStyle}>
                                            <Container
                                                maxWidth={settings.themeStretch ? false : 'xl'}
                                                sx={leadFooterButtonsStyle}
                                            >
                                                <Button
                                                    variant='contained'
                                                    onClick={() => failedToAttendClick()}
                                                    disabled={!activeTab}
                                                    sx={{
                                                bgcolor: '#00AB55',
                                                color: '#ffffff',
                                                fontSize: '13px',
                                                fontWeight: 600,
                                                letterSpacing: '-0.01em',
                                                textTransform: 'none',
                                                borderRadius: '6px',
                                                boxShadow: 'none',
                                                px: 2.25,
                                                py: 0.75,
                                                minWidth: '88px',
                                                '&:hover': { bgcolor: '#02934A', boxShadow: 'none' },
                                                '&:active': { bgcolor: '#017A3D' },
                                                '&.Mui-disabled': { bgcolor: 'rgba(0, 171, 85, 0.35)', color: '#ffffff' },
                                            }}
                                                >
                                                    Finish
                                                </Button>
                                            </Container>
                                        </Box>
                                        : null}
                                </>
                                : <Box sx={footerBoxStyle}
                                >
                                    <Container
                                        maxWidth={settings.themeStretch ? false : 'xl'}
                                        sx={leadFooterButtonsStyle}
                                    >
                                        <Button
                                            color="primary"
                                            variant='contained'
                                            onClick={handleNext}
                                            disabled={!activeTab}
                                            sx={{
                                                bgcolor: '#00AB55',
                                                color: '#ffffff',
                                                fontSize: '13px',
                                                fontWeight: 600,
                                                letterSpacing: '-0.01em',
                                                textTransform: 'none',
                                                borderRadius: '6px',
                                                boxShadow: 'none',
                                                px: 2.25,
                                                py: 0.75,
                                                minWidth: '88px',
                                                '&:hover': { bgcolor: '#02934A', boxShadow: 'none' },
                                                '&:active': { bgcolor: '#017A3D' },
                                                '&.Mui-disabled': { bgcolor: 'rgba(0, 171, 85, 0.35)', color: '#ffffff' },
                                            }}
                                        >
                                            Finish
                                        </Button>
                                    </Container>
                                </Box>}
                        </Card>
                    </Container>
                </Grid>
            </Stack>
        </Dialog>
    );
}

OpenUnAttendedPopup.propTypes = {
    open: PropTypes.bool,
    onClose: PropTypes.func,
    type: PropTypes.string,
    user: PropTypes.object,
    inputRef: PropTypes.any,
    headerButtons: PropTypes.bool,
    handleCloseAndReload: PropTypes.any,
};